// Import Vue for Vue.set
import Vue from 'vue';
import homepageApi from '@/api/homepage';
import productsApi from '@/api/products';
import { getUserFeeConfig } from '@/api/user-fee';
import { getSimpleProductOriginalPrice } from '@/lib/helper';

// Constants
const SET_CURRENT_STEP = 'SET_CURRENT_STEP';
const SET_SELECTED_FILE = 'SET_SELECTED_FILE';
const SET_SHIPPING_LABEL_FILE = 'SET_SHIPPING_LABEL_FILE';
const SET_SELECTED_SUPPLIER_ID = 'SET_SELECTED_SUPPLIER_ID';
const SET_SUPPLIERS = 'SET_SUPPLIERS';
const SET_SUPPLIER_PAGINATION = 'SET_SUPPLIER_PAGINATION';
const SET_CONFIG = 'SET_CONFIG';
const SET_MAPPING_DATA = 'SET_MAPPING_DATA';
const SET_MAPPING_ROW = 'SET_MAPPING_ROW';
const RESET_MAPPING_DATA = 'RESET_MAPPING_DATA';
const SET_AVAILABLE_PRODUCTS = 'SET_AVAILABLE_PRODUCTS';
const SET_CUSTOMER_INFO = 'SET_CUSTOMER_INFO';
const SET_SELECTED_PRODUCTS = 'SET_SELECTED_PRODUCTS';
const SET_SUMMARY_ITEMS = 'SET_SUMMARY_ITEMS';
const SET_SERVICE_FEE = 'SET_SERVICE_FEE';
const SET_IS_PROCESSING = 'SET_IS_PROCESSING';
const SET_IS_SUBMITTING = 'SET_IS_SUBMITTING';
const SET_ERROR = 'SET_ERROR';
const RESET_STATE = 'RESET_STATE';

const state = {
	// Wizard state
	currentStep: 1,
	totalSteps: 3,

	// Step 1: Configuration
	selectedFile: null,
	shippingLabelFile: null,
	selectedSupplierId: null,
	config: {
		skipHeader: true,
		validateProducts: true,
		autoFillPrice: true,
	},

	// Step 2: Product Mapping
	mappingDataPerProduct: [],
	mappingDataPerPackage: [],
	availableProducts: [],
	suppliers: [],
	supplierPagination: {
		currentPage: 1,
		totalPages: 1,
		totalSuppliers: 0,
		limit: 9,
	},

	// Step 3: Order Summary
	selectedProducts: [],
	summaryItems: [],
	customerInfo: {
		name: '',
		email: '',
		phone: '',
		notes: '',
	},
	serviceFee: null,

	// UI state
	isProcessing: false,
	isSubmitting: false,
	error: null,
};

const getters = {
	currentStep: state => state.currentStep,
	totalSteps: state => state.totalSteps,
	selectedFile: state => state.selectedFile,
	shippingLabelFile: state => state.shippingLabelFile,
	selectedSupplierId: state => state.selectedSupplierId,
	suppliers: state => state.suppliers,
	supplierPagination: state => state.supplierPagination,
	selectedSupplier: (state) => {
		if (!state.selectedSupplierId || !state.suppliers) return null;
		return state.suppliers.find(s => s._id === state.selectedSupplierId);
	},
	config: state => state.config,
	mappingDataPerProduct: state => state.mappingDataPerProduct,
	mappingDataPerPackage: state => state.mappingDataPerPackage,
	availableProducts: state => state.availableProducts,
	selectedProducts: state => state.selectedProducts,
	summaryItems: state => state.summaryItems,
	customerInfo: state => state.customerInfo,
	serviceFee: state => state.serviceFee,
	isProcessing: state => state.isProcessing,
	isSubmitting: state => state.isSubmitting,
	error: state => state.error,

	// Computed getters
	mappedCount: state => state.mappingDataPerProduct.reduce((sum, row) => {
		if (row.selectedProducts && row.selectedProducts.length > 0) {
			return sum + row.selectedProducts.length;
		}
		return sum;
	}, 0),
	unmappedCount: state => state.mappingDataPerProduct.filter(row => !row.isValid).length,
	totalQuantity: state => state.selectedProducts.reduce((sum, product) => sum + product.quantity, 0),
	totalAmount: state => state.selectedProducts.reduce((sum, product) => sum + product.subtotal, 0),
	canContinueStep1: state => state.selectedFile && state.shippingLabelFile && state.selectedSupplierId && state.mappingDataPerProduct.length > 0,
	canContinueStep2: (_, getters) => getters.mappedCount > 0 && getters.unmappedCount === 0,
	canContinueStep3: () => true, // No validation needed for step 3
	totalHandlingFee: (state) => {
		// Calculate handling fee dari mappingDataPerPackage
		let total = 0;

		state.mappingDataPerPackage.forEach((pkg) => {
			pkg.items.forEach((item) => {
				if (!item.isValid || !item.selectedProducts || item.selectedProducts.length === 0) {
					return;
				}
				// Calculate fee for each product in item
				item.selectedProducts.forEach((product) => {
					const feeConfig = product?.fee_config;
					if (!feeConfig) {
						return;
					}
					const feeType = feeConfig.type || 'per_order';
					const feeValue = feeConfig.value || 0;

					if (feeType === 'per_order') {
						total += feeValue;
					} else {
						total += feeValue * item.quantity;
					}
				});
			});
		});

		return total;
	},
};

const mutations = {
	[SET_CURRENT_STEP](state, step) {
		state.currentStep = step;
	},
	[SET_SELECTED_FILE](state, file) {
		state.selectedFile = file;
	},
	[SET_SHIPPING_LABEL_FILE](state, file) {
		state.shippingLabelFile = file;
	},
	[SET_SELECTED_SUPPLIER_ID](state, supplierId) {
		state.selectedSupplierId = supplierId;
	},
	[SET_SUPPLIERS](state, suppliers) {
		state.suppliers = suppliers;
	},
	[SET_SUPPLIER_PAGINATION](state, pagination) {
		state.supplierPagination = { ...state.supplierPagination, ...pagination };
	},
	[SET_CONFIG](state, config) {
		state.config = { ...state.config, ...config };
	},
	[SET_MAPPING_DATA](state, { perProduct, perPackage }) {
		state.mappingDataPerProduct = perProduct || [];
		state.mappingDataPerPackage = perPackage || [];
	},
	[SET_MAPPING_ROW](state, { index, row }) {
		Vue.set(state.mappingDataPerProduct, index, row);
	},
	[RESET_MAPPING_DATA](state) {
		state.mappingDataPerProduct = [];
		state.mappingDataPerPackage = [];
	},
	[SET_AVAILABLE_PRODUCTS](state, products) {
		state.availableProducts = products;
	},
	[SET_CUSTOMER_INFO](state, info) {
		state.customerInfo = { ...state.customerInfo, ...info };
	},
	[SET_SELECTED_PRODUCTS](state, products) {
		state.selectedProducts = products;
	},
	[SET_SUMMARY_ITEMS](state, items) {
		state.summaryItems = items;
	},
	[SET_SERVICE_FEE](state, serviceFee) {
		state.serviceFee = serviceFee;
	},
	[SET_IS_PROCESSING](state, value) {
		state.isProcessing = value;
	},
	[SET_IS_SUBMITTING](state, value) {
		state.isSubmitting = value;
	},
	[SET_ERROR](state, error) {
		state.error = error;
	},
	[RESET_STATE](state) {
		// Keep totalSteps
		const { totalSteps } = state;
		Object.assign(state, {
			currentStep: 1,
			selectedFile: null,
			shippingLabelFile: null,
			selectedSupplierId: null,
			suppliers: [],
			supplierPagination: {
				currentPage: 1,
				totalPages: 1,
				totalSuppliers: 0,
				limit: 9,
			},
			config: {
				skipHeader: true,
				validateProducts: true,
				autoFillPrice: true,
			},
			mappingDataPerProduct: [],
			mappingDataPerPackage: [],
			availableProducts: [],
			selectedProducts: [],
			summaryItems: [],
			customerInfo: {
				name: '',
				email: '',
				phone: '',
				notes: '',
			},
			serviceFee: null,
			isProcessing: false,
			isSubmitting: false,
			error: null,
			totalSteps,
		});
	},
};

const actions = {
	setCurrentStep({ commit }, step) {
		commit(SET_CURRENT_STEP, step);
	},

	nextStep({ commit, state }) {
		if (state.currentStep < state.totalSteps) {
			commit(SET_CURRENT_STEP, state.currentStep + 1);
		}
	},

	prevStep({ commit, state }) {
		if (state.currentStep > 1) {
			commit(SET_CURRENT_STEP, state.currentStep - 1);
		}
	},

	setSelectedFile({ commit }, file) {
		commit(SET_SELECTED_FILE, file);
	},

	setShippingLabelFile({ commit }, file) {
		commit(SET_SHIPPING_LABEL_FILE, file);
	},

	setSelectedSupplierId({ commit }, supplierId) {
		commit(SET_SELECTED_SUPPLIER_ID, supplierId);
	},

	setSuppliers({ commit }, suppliers) {
		commit(SET_SUPPLIERS, suppliers);
	},

	setConfig({ commit }, config) {
		commit(SET_CONFIG, config);
	},

	setMappingData({ commit }, data) {
		commit(SET_MAPPING_DATA, data);
	},

	updateMappingRow({ commit, state }, { index, productIds }) {
		const row = { ...state.mappingDataPerProduct[index] };

		if (productIds !== undefined) {
			// Extract IDs from product objects (vue-multiselect sends objects with track-by)
			const idsArray = Array.isArray(productIds)
				? productIds.map(p => p._id || p) // Extract _id if object, or use directly if ID
				: [productIds];
			row.selectedProductIds = idsArray;

			// Get all selected products
			const products = state.availableProducts.filter(p => idsArray.includes(p._id));
			row.selectedProducts = products;

			// Valid if at least one product is selected
			row.isValid = products.length > 0;
		}

		// Update the product mapping row
		commit(SET_MAPPING_ROW, { index, row });
	},

	resetMappingData({ commit }) {
		commit(RESET_MAPPING_DATA);
	},

	setAvailableProducts({ commit }, products) {
		commit(SET_AVAILABLE_PRODUCTS, products);
	},

	setCustomerInfo({ commit }, info) {
		commit(SET_CUSTOMER_INFO, info);
	},

	setSummaryItems({ commit }, items) {
		commit(SET_SUMMARY_ITEMS, items);
	},

	prepareSummary({ commit, state }) {
		const items = [];
		const selectedProductsMap = new Map();

		// Process mappingDataPerProduct to build summary items
		state.mappingDataPerProduct.forEach((row) => {
			if (row.isValid && row.selectedProducts && row.selectedProducts.length > 0) {
				// Add each product from the bundle
				row.selectedProducts.forEach((product) => {
					let basePrice = product.price || 0;
					if (product.type === 'simple') {
						const commissionConfigValue = getSimpleProductOriginalPrice(product);
						if (commissionConfigValue) {
							basePrice = commissionConfigValue;
						}
					}

					const quantity = row.quantity || 1;
					const subtotal = basePrice * quantity;

					const summaryItem = {
						productId: product._id,
						productName: product.name,
						productSku: product.code,
						price: product.price,
						quantity,
						subtotal,
					};
					items.push(summaryItem);

					// Group selectedProducts by product ID
					if (selectedProductsMap.has(product._id)) {
						// Product already exists, update quantity and subtotal
						const existing = selectedProductsMap.get(product._id);
						existing.quantity += quantity;
						existing.subtotal += subtotal;
					} else {
						// New product, add to map
						selectedProductsMap.set(product._id, {
							...product,
							quantity,
							subtotal,
						});
					}
				});
			}
		});

		// Convert map to array
		const selectedProducts = Array.from(selectedProductsMap.values());

		// Update mappingDataPerPackage with selectedProductIds and selectedProducts in each item
		// This allows mappingDataPerPackage items to have the same structure as mappingDataPerProduct rows
		const updatedPackages = state.mappingDataPerPackage.map((pkg) => {
			// Update each item in the package with selectedProductIds and selectedProducts
			const updatedItems = pkg.items.map((item) => {
				// Find matching products from mappingDataPerProduct based on productName only
				// Note: mappingDataPerProduct aggregates quantities by productName, so we match by name
				// and the mapping data applies to all items with that product name
				const matchedRow = state.mappingDataPerProduct.find(
					row => row.productName === item.productName,
				);

				return {
					...item,
					selectedProductIds: matchedRow?.selectedProductIds || [],
					selectedProducts: matchedRow?.selectedProducts || [],
					isValid: matchedRow?.isValid || false,
				};
			});

			return {
				...pkg,
				items: updatedItems,
			};
		});

		commit(SET_SELECTED_PRODUCTS, selectedProducts);
		commit(SET_SUMMARY_ITEMS, items);
		commit(SET_MAPPING_DATA, {
			perProduct: state.mappingDataPerProduct,
			perPackage: updatedPackages,
		});
	},

	setIsProcessing({ commit }, value) {
		commit(SET_IS_PROCESSING, value);
	},

	setIsSubmitting({ commit }, value) {
		commit(SET_IS_SUBMITTING, value);
	},

	setError({ commit }, error) {
		commit(SET_ERROR, error);
	},

	setServiceFee({ commit }, serviceFee) {
		commit(SET_SERVICE_FEE, serviceFee);
	},

	async fetchServiceFee({ commit, state }) {
		try {
			const params = {
				total_product_price: state.selectedProducts.reduce((sum, product) => sum + product.subtotal, 0),
			};

			return new Promise((resolve, reject) => {
				const callback = (response) => {
					const responseData = response.data;
					const serviceFee = responseData.service_fee || null;
					commit(SET_SERVICE_FEE, serviceFee);
					resolve(serviceFee);
				};

				const errorCallback = (err) => {
					console.log('Error fetching service fee:', err);
					reject(err);
				};

				getUserFeeConfig(params, callback, errorCallback);
			});
		} catch (error) {
			console.log('Error in fetchServiceFee action:', error);
			throw error;
		}
	},

	resetState({ commit }) {
		commit(RESET_STATE);
	},

	autoMatchProducts({ commit, state }) {
		const { mappingDataPerProduct, availableProducts } = state;

		if (!mappingDataPerProduct.length || !availableProducts.length) {
			return { matched: 0, total: mappingDataPerProduct.length };
		}

		// Create a map of available products by lowercase name for faster lookup
		const productMap = new Map();
		availableProducts.forEach((product) => {
			const normalizedName = product.name.toLowerCase().trim();
			if (!productMap.has(normalizedName)) {
				productMap.set(normalizedName, []);
			}
			productMap.get(normalizedName).push(product);
		});

		// Create updated mapping data with auto-matched products
		const updatedMappingData = mappingDataPerProduct.map((row) => {
			const normalizedSearch = row.productName.toLowerCase().trim();

			// Try exact match first
			let matchedProducts = productMap.get(normalizedSearch);

			// If no exact match, try partial match
			if (!matchedProducts || matchedProducts.length === 0) {
				const productEntries = Array.from(productMap.entries());
				matchedProducts = productEntries.find(([productName]) => productName.includes(normalizedSearch) || normalizedSearch.includes(productName))?.[1];
			}

			// Auto-select if there are matches
			if (matchedProducts && matchedProducts.length > 0) {
				return {
					...row,
					selectedProductIds: matchedProducts.map(p => p._id),
					selectedProducts: matchedProducts,
					isValid: true,
				};
			}

			return row;
		});

		// Update all rows in mapping data
		updatedMappingData.forEach((row, index) => {
			commit(SET_MAPPING_ROW, { index, row });
		});

		const matchedCount = updatedMappingData.filter(row => row.isValid).length;

		return {
			matched: matchedCount,
			total: updatedMappingData.length,
		};
	},

	async loadSuppliers({ commit }, { keyword, page = 1 } = {}) {
		try {
			return new Promise((resolve) => {
				const params = {
					page,
					limit: 9,
				};
				if (keyword) {
					params.keyword = keyword;
				}

				homepageApi.getPublicSuppliers(
					params,
					(response) => {
						// Access suppliers from nested data structure
						const suppliersList = response.data?.suppliers || [];
						const pageInfo = response.data?.page_info || {};

						const suppliers = suppliersList.map((supplier) => {
							const supplierId = supplier._id && supplier._id.$oid
								? supplier._id.$oid
								: supplier._id;

							let address = '';
							if (supplier.store && supplier.store.shipping && supplier.store.shipping.origin) {
								const { city, province } = supplier.store.shipping.origin;
								const parts = [
									city?.name,
									province?.name,
								].filter(Boolean);
								address = parts.join(', ');
							}

							return {
								_id: supplierId,
								code: supplier.code,
								name: supplier.name,
								address,
								logo: null,
								availability: supplier.store?.availability || false,
							};
						});

						commit(SET_SUPPLIERS, suppliers);
						commit(SET_SUPPLIER_PAGINATION, {
							currentPage: pageInfo.current_page || 1,
							totalPages: pageInfo.last_page || 1,
							totalSuppliers: pageInfo.total_suppliers || 0,
						});
						resolve(suppliers);
					},
					(error) => {
						// On error, treat as empty suppliers instead of rejecting
						console.error('Failed to load suppliers:', error);
						commit(SET_SUPPLIERS, []);
						commit(SET_SUPPLIER_PAGINATION, {
							currentPage: 1,
							totalPages: 1,
							totalSuppliers: 0,
						});
						resolve([]);
					},
				);
			});
		} catch (error) {
			// On any exception, treat as empty suppliers
			console.error('Exception in loadSuppliers:', error);
			commit(SET_SUPPLIERS, []);
			commit(SET_SUPPLIER_PAGINATION, {
				currentPage: 1,
				totalPages: 1,
				totalSuppliers: 0,
			});
			return [];
		}
	},

	async loadAvailableProducts({ commit }, { supplierCode } = {}) {
		try {
			return new Promise((resolve) => {
				const params = {
					limit: 100,
					sort_by: 'created_at',
					type: 'supplier',
				};

				if (supplierCode) {
					params.supplier_code = supplierCode;
				}

				productsApi.getPublicProducts(
					params,
					(response) => {
						// Access products from nested data structure
						const productsList = response.data?.products || [];

						// Transform products to match the expected structure
						const products = productsList.map((product) => {
							const productId = product._id && product._id.$oid
								? product._id.$oid
								: product._id;

							return {
								_id: productId,
								name: product.name,
								code: product.code,
								price: product.price,
								picture: product.picture || product.pictures?.[0] || null,
								stock: product.stock?.quantity || 0,
								category: product.category?.category || '',
								type: product.type,
								variations: product.variations || null,
								user: product.user || null,
								fee_config: product.fee_config || null,
								commission_config: product.commission_config || null,
							};
						});

						commit(SET_AVAILABLE_PRODUCTS, products);
						resolve(products);
					},
					(error) => {
						// On error, treat as empty products instead of rejecting
						console.error('Failed to load products:', error);
						commit(SET_AVAILABLE_PRODUCTS, []);
						resolve([]);
					},
				);
			});
		} catch (error) {
			// On any exception, treat as empty products
			console.error('Exception in loadAvailableProducts:', error);
			commit(SET_AVAILABLE_PRODUCTS, []);
			return [];
		}
	},
};

export default {
	namespaced: true,
	state,
	getters,
	mutations,
	actions,
};
